import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { surgeries, specialties, locations } from "@/lib/healic-data";
import { SurgeryPage, type SurgeryLoaderData } from "@/routes/surgeries.$slug";
import { loadLegacyContent, LegacyContent, legacyHead, type LegacyPayload } from "@/lib/legacy-content";

const SITE_URL = "https://healic.in";

function shortCity(city: string): string {
  return city.split(",")[0]!.trim();
}

/**
 * Legacy city-level URLs we still serve at /gynecology-surgeries/<slug>.
 * Each maps to a surgery + location so we can render the same SurgeryPage
 * layout used by other surgery pages, with city-specific SEO meta.
 */
const LEGACY_LOCATION_PAGES: Record<
  string,
  { surgery: string; location: string; heading: string; intro: string }
> = {
  "dnc-procedure-in-agra": {
    surgery: "dandc-mtp-procedures",
    location: "agra",
    heading: "D&C Procedure in Agra",
    intro:
      "Healic Agra is one of the most trusted gynaecology centres in the city for safe, confidential D&C and MTP procedures. Patients from across Agra, Fatehabad Road, Sikandra, Dayalbagh and nearby towns of Mathura, Firozabad and Etah choose us for ultrasound-guided care, female-only consultation rooms, fixed-price packages, and complete legal & medical privacy under our senior gynaecology team.",
  },
  "dnc-procedure-in-gurugram": {
    surgery: "dandc-mtp-procedures",
    location: "sohna-road-gurugram",
    heading: "D&C Procedure in Gurugram",
    intro:
      "Healic Gurugram (Sohna Road) offers safe, confidential and gynaecologist-led D&C and MTP procedures for women from Sohna Road, Sector 49, Badshahpur, Golf Course Extension and across Gurgaon-NCR. We follow strict MTP Act protocols with ultrasound confirmation, daycare admission and discreet follow-up — most patients are home within a few hours and back to routine within 2–3 days.",
  },
  "dnc-procedure-in-haridwar": {
    surgery: "dandc-mtp-procedures",
    location: "haridwar",
    heading: "D&C Procedure in Haridwar",
    intro:
      "Healic Haridwar provides safe, confidential D&C and MTP procedures for women from Haridwar, Roorkee, Jwalapur, Kankhal and Rishikesh. Our senior gynaecology team follows MTP Act protocols with ultrasound confirmation, female-only consultation rooms, daycare admission and discreet, judgment-free follow-up — ideal for travelling patients who want the procedure handled in a single, private visit.",
  },
};

type LoaderResult =
  | {
      kind: "city";
      data: SurgeryLoaderData;
      cfg: (typeof LEGACY_LOCATION_PAGES)[string];
      locationName: string;
    }
  | { kind: "legacy"; payload: LegacyPayload };

export const Route = createFileRoute("/gynecology-surgeries/$slug")({
  loader: ({ params }): LoaderResult => {
    const cfg = LEGACY_LOCATION_PAGES[params.slug];
    if (cfg) {
      const surgery = surgeries.find((s) => s.slug === cfg.surgery);
      const location = locations.find((l) => l.slug === cfg.location);
      if (!surgery || !location) throw notFound();
      const specialty = specialties.find((sp) => sp.slug === surgery.specialtySlug);
      const related = surgeries
        .filter((s) => s.specialtySlug === surgery.specialtySlug && s.slug !== surgery.slug)
        .slice(0, 4);
      return {
        kind: "city",
        data: { surgery, specialty, related },
        cfg,
        locationName: shortCity(location.city),
      };
    }
    return { kind: "legacy", payload: loadLegacyContent(`gynecology-surgeries/${params.slug}`) };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Healic" }] };
    if (loaderData.kind === "legacy") {
      return legacyHead(`/gynecology-surgeries/${params.slug}`, loaderData.payload);
    }
    const { cfg, locationName, data } = loaderData;
    const path = `/gynecology-surgeries/${params.slug}`;
    const url = `${SITE_URL}${path}`;
    const title = `${cfg.heading} — Cost, Best Gynaecologists & Recovery | Healic`;
    const description = `${cfg.heading} at Healic ${locationName}. Confidential, ultrasound-guided D&C / MTP with expert gynaecologists, fixed-price packages and complete privacy.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "geo.placename", content: locationName },
        { property: "og:title", content: `${cfg.heading} — Healic` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: "Healic Multispeciality Hospital" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${cfg.heading} — Healic` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: cfg.heading,
            description: data.surgery.overview,
            procedureType: "https://schema.org/SurgicalProcedure",
            howPerformed: data.surgery.procedure.join(". "),
            followup: data.surgery.recovery,
            url,
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Page not found</h1>
        <Link to="/surgeries" className="mt-6 inline-block text-accent">Browse all treatments →</Link>
      </div>
    </PageShell>
  ),
  errorComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Something went wrong</h1>
      </div>
    </PageShell>
  ),
  component: LegacyGynaePage,
});

function LegacyGynaePage() {
  const data = Route.useLoaderData();
  if (data.kind === "legacy") return <LegacyContent payload={data.payload} />;
  return <SurgeryPage data={data.data} />;
}
